import { isAddressEquivalent } from '@funkit/utils'
import type { AvailableChain } from './availableChains'
import type { SupportedToken } from './supportedTokens'

export interface DepositSourceToken extends SupportedToken {
  chainId: number
  address: string
  decimals: number
}
export interface DepositAsset {
  id: string
  symbol: string
  tokens: DepositSourceToken[]
}

export interface DepositAssetExclusion {
  symbol: string
  chainId?: number
  reason: 'invalid-metadata' | 'ambiguous-contract'
  conflictingAddresses?: string[]
}

export function assetId(symbol: string): string {
  return symbol.toUpperCase()
}

export function depositAddressesEqual(left?: string, right?: string) {
  if (typeof left !== 'string' || typeof right !== 'string') {
    return false
  }
  return isAddressEquivalent({ firstAddress: left, secondAddress: right })
}

function resolveDecimals(
  configured: SupportedToken,
  metadata: SupportedToken[],
): number | undefined {
  return (
    configured.decimals ??
    metadata.find(
      (entry) =>
        entry.chainId === configured.chainId &&
        depositAddressesEqual(entry.address, configured.address),
    )?.decimals
  )
}

function isValidChainId(chainId: number | undefined): chainId is number {
  return chainId !== undefined && Number.isSafeInteger(chainId) && chainId > 0
}

function isValidDecimals(decimals: number | undefined): decimals is number {
  return (
    decimals !== undefined &&
    Number.isInteger(decimals) &&
    decimals >= 0 &&
    decimals <= 255
  )
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && Boolean(value.trim())
}

function toSourceToken(
  token: SupportedToken,
  decimals: number | undefined,
): DepositSourceToken | undefined {
  const { chainId, address, symbol } = token
  if (
    !isValidChainId(chainId) ||
    !isNonBlankString(address) ||
    !isValidDecimals(decimals) ||
    !isNonBlankString(symbol)
  ) {
    return undefined
  }
  return { ...token, chainId, address, decimals }
}

type DepositAssetInput = DepositSourceToken | DepositAssetExclusion

function groupBySymbol(inputs: DepositAssetInput[]): {
  assets: DepositAsset[]
  exclusions: DepositAssetExclusion[]
} {
  const groups = new Map<string, DepositAsset>()
  const ambiguous = new Set<string>()
  const exclusions: DepositAssetExclusion[] = []
  for (const input of inputs) {
    if ('reason' in input) {
      exclusions.push(input)
      continue
    }
    const id = assetId(input.symbol)
    const group = groups.get(id) ?? { id, symbol: input.symbol, tokens: [] }
    const networkKey = `${id}:${input.chainId}`
    if (ambiguous.has(networkKey)) {
      continue
    }
    const existing = group.tokens.find(
      (entry) => entry.chainId === input.chainId,
    )
    if (existing) {
      if (depositAddressesEqual(existing.address, input.address)) {
        continue
      }
      group.tokens = group.tokens.filter(
        (entry) => entry.chainId !== input.chainId,
      )
      exclusions.push({
        symbol: input.symbol,
        chainId: input.chainId,
        reason: 'ambiguous-contract',
        conflictingAddresses: [existing.address, input.address],
      })
      ambiguous.add(networkKey)
      continue
    }
    group.tokens.push(input)
    groups.set(id, group)
  }
  return {
    assets: [...groups.values()].filter((group) => group.tokens.length > 0),
    exclusions,
  }
}

/** Group curated tickers; exclude all contracts for a symbol/network collision. */
export function depositAssets(
  tokens: SupportedToken[],
  chains: AvailableChain[],
  metadata: SupportedToken[] = tokens,
): { assets: DepositAsset[]; exclusions: DepositAssetExclusion[] } {
  const enabled = new Set(chains.map((chain) => chain.id))
  const inputs: DepositAssetInput[] = []
  for (const configured of tokens) {
    // A missing chain ID is invalid metadata, not a disabled network.
    if (
      configured.chainId !== undefined &&
      !enabled.has(String(configured.chainId))
    ) {
      continue
    }
    const token = toSourceToken(
      configured,
      resolveDecimals(configured, metadata),
    )
    inputs.push(
      token ?? {
        symbol: configured.symbol,
        chainId: configured.chainId,
        reason: 'invalid-metadata',
      },
    )
  }
  return groupBySymbol(inputs)
}

export function assetChains(
  assets: DepositAsset[],
  chains: AvailableChain[],
): AvailableChain[] {
  return chains.filter((chain) =>
    assets.some((asset) => tokenOnChain(asset, chain) !== undefined),
  )
}

export function tokenOnChain(
  asset: DepositAsset,
  chain: AvailableChain,
): DepositSourceToken | undefined {
  return asset.tokens.find((token) => String(token.chainId) === chain.id)
}

export function resolveDepositSelection(
  assets: DepositAsset[],
  chains: AvailableChain[],
  selection: { symbol: string; chainId: number },
): { token: DepositSourceToken; chain: AvailableChain } | undefined {
  const asset = assets.find((entry) => entry.id === assetId(selection.symbol))
  const chain = chains.find((entry) => Number(entry.id) === selection.chainId)
  if (!asset || !chain) {
    return undefined
  }
  const token = tokenOnChain(asset, chain)
  return token ? { token, chain } : undefined
}
